import classNames from "classnames/bind";
import { filter, get } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import FontAwesome from "react-fontawesome";
import Select, {
  components,
  createFilter,
  type DropdownIndicatorProps,
  type GroupBase,
  type StylesConfig,
  type ValueContainerProps,
} from "react-select";
import { Popover } from "react-tiny-popover";
import { type OptionType } from "../Form/types";
import Pill from "../Pill/Pill";
import styles from "../constants/index";
import cssStyles from "./MultipleDropdownInputWithsearch.module.scss";
import {
  type MultiDropdownInputWithSearchHintProps,
  type MultiDropdownInputWithSearchIndicatorProps,
  type MultiDropdownInputWithSearchProps,
} from "./types";

const cx = classNames.bind(cssStyles);

const INPUT_WIDTH = 50;

const OptionHint = ({ limit }: MultiDropdownInputWithSearchHintProps) => {
  return (
    <div className={cx("optionHint")}>
      You can only select {limit} options or less
    </div>
  );
};

const DropdownIndicator = (
  props: DropdownIndicatorProps<any> &
    MultiDropdownInputWithSearchIndicatorProps
) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { isMulti, selectLimit, value } = props.selectProps;

  return isMulti && selectLimit && value?.length > selectLimit ? (
    <Popover
      isOpen={isPopoverOpen}
      positions={["right", "left"]}
      align="center"
      content={<OptionHint limit={selectLimit} />}
    >
      <div
        className={cx("icon", "icon--error")}
        onMouseEnter={() => setIsPopoverOpen(true)}
        onMouseLeave={() => setIsPopoverOpen(false)}
      >
        <FontAwesome name="exclamation-circle" />
      </div>
    </Popover>
  ) : (
    <components.DropdownIndicator {...props} />
  );
};

function getOptionsWithLabels(
  options: any,
  labelKey?: string,
  customLabelFormat?: (value: string | Record<string, any>) => string
) {
  if (labelKey) {
    if (Array.isArray(options)) {
      return options.map((option: any) => {
        const label = get(option, labelKey);

        option.label = customLabelFormat ? customLabelFormat(label) : label;
        option.value = label;
        return option;
      });
    } else {
      const label = get(options, labelKey);

      options.label = customLabelFormat ? customLabelFormat(label) : label;
      options.value = label;
      return options;
    }
  } else {
    if (Array.isArray(options)) {
      return filter(options, (option: any) => option).map((option: any) => ({
        label: option,
        value: option,
      }));
    } else {
      return { label: options, value: options };
    }
  }
}

const customStyles: StylesConfig<unknown, true, GroupBase<any>> = {
  container: (provided) => {
    return {
      ...provided,
      display: "inline-block",
    };
  },
  control: (provided, state) => {
    let borderStyles = { border: `1px solid ${styles.surface["100"]}` };

    if (state.isDisabled) {
      borderStyles = { border: `1px solid ${styles.surface["200"]}` };
    } else if (state.isFocused) {
      borderStyles = { border: `1px solid ${styles.navy["500"]}` };
    }

    return {
      ...provided,
      cursor: "pointer",
      borderRadius: "6px",
      color: state.isDisabled ? styles.text["400"] : `${styles.text["800"]}`,
      caretColor: `${styles.navy["500"]}`,
      boxShadow: "none",
      "&:hover": {
        border: `1px solid ${styles.navy["500"]}`,
      },
      ...borderStyles,
    };
  },
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({ ...provided, margin: 0 }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: "5px",
    // @ts-expect-error unreachable type
    minWidth: state.selectProps.width,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: 0,
    fill: styles.surface["800"],
    stroke: styles.surface["800"],
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: "0px 5px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
    transition: "transform 0.3s",
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? styles.navy["50"]
      : state.isFocused
        ? styles.surface["75"]
        : "#fff",
    ":active": {
      backgroundColor: styles.navy["50"],
    },
    padding: "8px 12px",
    color: styles.text["800"],
    position: "relative",
  }),
  menu: (provided, state) => {
    return {
      ...provided,
      width: "max-content",
      maxWidth: "350px",
      marginTop: "12px",
      zIndex: styles.zIndexPopover,
      // @ts-expect-error unreachable type
      right: state.selectProps.align === "right" ? 0 : undefined,
    };
  },
};

const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
  const { length } = props.getValue();

  const selectOptionsAndInput = React.Children.toArray(children);
  const searchInput = selectOptionsAndInput?.[selectOptionsAndInput.length - 1];

  if (length && props.isMulti) {
    return (
      <components.ValueContainer {...props}>
        <Pill variant="default">
          {props.selectProps.placeholder} {length}
        </Pill>
        {searchInput}
      </components.ValueContainer>
    );
  }

  return (
    <components.ValueContainer {...props}>
      {selectOptionsAndInput}
    </components.ValueContainer>
  );
};

/**
 * @deprecated use "Dropdown" instead (see Storybook documentation)
 */
const MultiDropdownInputWithSearch = <T,>({
  options,
  label,
  labelKey,
  dropdownKey,
  onChange,
  value,
  isDisabled,
  width = 50,
  selectLimit,
  customLabelFormat,
  isMulti = true,
  isClearable = false,
  align = "left",
}: MultiDropdownInputWithSearchProps<T>) => {
  const [interimValue, setInterimValue] = useState<T[] | undefined>(undefined);

  const option = useMemo(
    () => getOptionsWithLabels(options, labelKey, customLabelFormat),
    [options, labelKey, customLabelFormat]
  );

  const onMenuOpen = () => {
    if (isMulti) {
      setInterimValue(value);
    }
  };

  const onMenuClose = () => {
    if (interimValue && (!selectLimit || interimValue?.length <= selectLimit)) {
      onChange(interimValue, dropdownKey);
    }

    setInterimValue(undefined);
  };

  const onMultipleSelectChange = (selectedValues: any) => {
    if (!labelKey) {
      selectedValues =
        selectedValues?.map((value: OptionType) => value.label) ?? [];
    }

    setInterimValue(selectedValues);
  };

  const onSelectChange = (selectedValue: any) => {
    if (!labelKey) {
      selectedValue = selectedValue?.value;
    }

    onChange(selectedValue);
  };

  const selectValue = interimValue ?? value ?? [];

  return (
    <Select
      styles={customStyles}
      value={getOptionsWithLabels(selectValue, labelKey)}
      options={option}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      onChange={isMulti ? onMultipleSelectChange : onSelectChange}
      filterOption={createFilter({
        ignoreAccents: false,
        ignoreCase: true,
      })}
      isDisabled={isDisabled}
      closeMenuOnSelect={!isMulti}
      isMulti={isMulti}
      backspaceRemovesValue={false}
      placeholder={label}
      hideSelectedOptions={false}
      isClearable={isClearable}
      // @ts-expect-error Not sure why the type of DropdownIndicator is wrong
      components={{ DropdownIndicator, ValueContainer }}
      // extra props to pass to custom components
      width={width && width >= INPUT_WIDTH ? width : INPUT_WIDTH}
      selectLimit={selectLimit}
      align={align}
    />
  );
};

export default MultiDropdownInputWithSearch;
